import React from "react";
import { Avatar, Tooltip } from "@fluentui/react-components";
import { Mail, Phone } from "lucide-react";
import HoverCard from "../hoverCard/HoverCard";
import useNavigation from "../../utils/useNavigation";
import {
  entityTypeToParentType,
  getLabelByType,
  formatPhoneForTelHref,
  formatPhoneNumberForDisplay,
} from "../../helpers/utils";
import styles from "./UserOrgChart.module.scss";

const byName = (a, b) => (a.name || "").localeCompare(b.name || "");

// Builds a manager tree (roots + children-by-manager map) out of the flat user list of one node.
// A user whose manager isn't in the same list becomes a root: they either have no manager, or the
// backend dropped the edge (OrgChartSettings.IncludeManagersOutsideScope off).
const buildTree = (users) => {
  const ids = new Set(users.map((u) => u.id));
  const childrenByManager = new Map();
  const roots = [];

  users.forEach((u) => {
    if (u.managerId && ids.has(u.managerId)) {
      if (!childrenByManager.has(u.managerId)) childrenByManager.set(u.managerId, []);
      childrenByManager.get(u.managerId).push(u);
    } else {
      roots.push(u);
    }
  });

  roots.sort(byName);
  childrenByManager.forEach((list) => list.sort(byName));

  return { roots, childrenByManager };
};

// A row of children hanging from the node above: a trunk down from it and, drawn by each child (see
// .treeItem), a bar across the siblings with a stub into every one of them. A lone child gets one
// continuous line instead, so no two line pieces have to meet at the same pixel.
const ChildRow = ({ items }) => {
  const single = items.length === 1;
  return (
    <>
      <div className={single ? styles.orgTrunkLong : styles.orgTrunk} />
      <div className={`${styles.treeChildren} ${single ? styles.treeChildrenSingle : ""}`}>{items}</div>
    </>
  );
};

// One node of the tree plus, underneath it, its children joined by tree lines.
const TreeBranch = ({ node, children, isRoot = false }) => {
  const kids = React.Children.toArray(children);
  return (
    <div className={isRoot ? styles.treeRoot : styles.treeItem}>
      {node}
      {kids.length > 0 && <ChildRow items={kids} />}
    </div>
  );
};

const renderChip = (user) => {
  const chip = (
    <div className={`${styles.chartChip} ${!user.isDirect ? styles.chartChipExternal : ""}`}>
      <HoverCard
        HoverCardTrigger={<Avatar color="colorful" size={24} name={user.name} image={{ src: user.image || "" }} />}
        AvatarId={user.id}
        AvatarName={user.name}
        AvatarEmail={user.email}
        AvatarPhone={user.phoneNumber}
        AvatarIsExternal={user.isExternal}
        CanSendMessage={false}
      />
      <div className={styles.chartChipText}>
        <span className={styles.chartChipName}>{user.name}</span>
        {user.title && <span className={styles.chartChipTitle}>{user.title}</span>}
      </div>
      {user.email && (
        <Tooltip content={user.email} relationship="label">
          <a href={`mailto:${user.email}`} className={styles.chipIconBtn}>
            <Mail size={12} />
          </a>
        </Tooltip>
      )}
      {user.phoneNumber && (
        <Tooltip content={formatPhoneNumberForDisplay(user.phoneNumber)} relationship="label">
          <a href={formatPhoneForTelHref(user.phoneNumber)} className={styles.chipIconBtn}>
            <Phone size={12} />
          </a>
        </Tooltip>
      )}
    </div>
  );

  return user.isDirect ? (
    chip
  ) : (
    <Tooltip content="Not assigned to this entity" relationship="description">
      {chip}
    </Tooltip>
  );
};

// The direct resources of one entity as a card holding their manager tree: managers on top, their
// direct reports beneath them. Several top-level people sit side by side and wrap onto more rows.
const resourcesCard = (users) => {
  if (!users || users.length === 0) return null;

  const { roots, childrenByManager } = buildTree(users);

  const renderPerson = (user, isRoot) => (
    <TreeBranch key={user.id} isRoot={isRoot} node={renderChip(user)}>
      {(childrenByManager.get(user.id) || []).map((report) => renderPerson(report, false))}
    </TreeBranch>
  );

  return (
    <div className={styles.resourcesCard}>
      <div className={styles.resourceRoots}>{roots.map((root) => renderPerson(root, true))}</div>
    </div>
  );
};

// One entity: its card, its resources card directly under it, then the entities linked to it
// (drawn as a row beneath the resources). Entities below the root are items of their parent's row.
const EntityBranch = ({ card, users, linked = [], isRoot = false }) => {
  const resources = resourcesCard(users);
  return (
    <div className={isRoot ? styles.treeRoot : styles.treeItem}>
      {card}
      {resources && (
        <>
          <div className={styles.orgTrunk} />
          {resources}
        </>
      )}
      {linked.length > 0 && <ChildRow items={linked} />}
    </div>
  );
};

const UserOrgChart = ({ scope, data, isLoading, currentId, currentName, onNavigate }) => {
  const { navigateTo } = useNavigation();

  if (isLoading) {
    return <p className={styles.emptyState}>Loading org chart...</p>;
  }

  const isHierarchy = scope === "Hierarchy";
  const nodes = data?.hierarchy || [];
  const currentUsers = isHierarchy ? nodes.find((n) => n.owner?.id === currentId)?.users || [] : data?.team || [];
  const descendants = isHierarchy ? nodes.filter((n) => n.owner?.id !== currentId) : [];
  const allUsers = isHierarchy ? nodes.flatMap((n) => n.users) : currentUsers;
  const hasOutsideManagers = allUsers.some((u) => !u.isDirect);

  const goTo = (type, id, name) => {
    onNavigate?.();
    navigateTo(entityTypeToParentType(type), id, name);
  };

  const emptyText = <span className={`${styles.emptyText} text-xsm-400 grey46Color`}>No direct resources</span>;

  const rootCard = (
    <div className={`${styles.orgRoot} ${styles.orgRootCurrent}`}>
      <p className={styles.orgNodeTitle}>
        <span className={styles.currentName}>{currentName || "This entity"}</span>
      </p>
      {currentUsers.length === 0 && emptyText}
    </div>
  );

  const entityCard = (node) => (
    <div className={styles.orgNode}>
      <p className={styles.orgNodeTitle}>
        <Tooltip content={`Go to ${node.owner?.name || "entity"}`} relationship="description">
          <span
            className={styles.entityBadge}
            onClick={() => goTo(node.owner?.type, node.owner?.id, node.owner?.name)}
          >
            <span className={styles.badgeLabel}>{getLabelByType("entityType", node.owner?.type)}</span>
            <span className={styles.badgeName}>{node.owner?.name}</span>
          </span>
        </Tooltip>
      </p>
      {node.users.length === 0 && emptyText}
    </div>
  );

  return (
    <div className={styles.orgChart}>
      <div className={styles.orgChartInner}>
        <EntityBranch
          isRoot
          card={rootCard}
          users={currentUsers}
          linked={descendants.map((node) => (
            <EntityBranch key={node.owner?.id} card={entityCard(node)} users={node.users} />
          ))}
        />

        {hasOutsideManagers && (
          <p className={styles.legend}>
            <span className={styles.legendSwatch} /> Dashed pill: manager who is not assigned to this entity
          </p>
        )}
      </div>
    </div>
  );
};

export default UserOrgChart;
